/**
 * LogisticsDialog Component
 *
 * Dialog for LOGISTIC users to update real waste collection values and upload process documents.
 * Uses react-hook-form with Zod validation and shadcn/ui components.
 *
 * Refactored: 2025-11-02 (Phase 5 - Admin Forms Refactoring)
 * - Replaced manual state management with useLogisticsDialog hook
 * - Replaced inline inputs with shadcn/ui Input + FormField
 * - Applied design system utilities from main.css
 * - Improved validation feedback
 */

import { FaUpload, FaTrash } from 'react-icons/fa6';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useLogisticsDialog } from '../hooks/useLogisticsDialog';
import type { WasteCollectionFormReportDTO } from '../api/waste-collection.api';
import type { LogisticsFormData } from '../validation/logistics.schemas';

interface LogisticsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (values: LogisticsFormData) => Promise<void>;
    orderData: WasteCollectionFormReportDTO;
    loading?: boolean;
}

export function LogisticsDialog({
    isOpen,
    onClose,
    onSave,
    orderData,
    loading = false
}: LogisticsDialogProps) {
    const {
        form,
        onSubmit,
        total,
        handleDeeeUpload,
        handleDbaUpload,
        uploadingDeee,
        uploadingDba,
        removeDeeeDocument,
        removeDbaDocument,
    } = useLogisticsDialog({
        orderData,
        isOpen,
        onSave: async (data) => {
            await onSave(data);
            onClose();
        },
        loading,
    });

    // Get form values for display
    const deeeDoc = form.watch('deeeOrderDocument');
    const dbaDoc = form.watch('dbaOrderDocument');

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Actualizare Valori Reale Colectate</DialogTitle>
                    <DialogDescription>
                        Introduceți valorile reale colectate și încărcați documentele proces.
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={onSubmit} className="space-y-6">
                        {/* School Info */}
                        <div className="bg-gray-50 p-3 rounded-sm">
                            <p className="text-sm font-semibold text-gray-700">
                                {orderData.school?.numeInstitutieInvatamant || 'Școală necunoscută'}
                            </p>
                            {/* Era `text-xs` (12px): CUI-ul si codul SE sunt datele dupa
                                care logistica identifica scoala, nu decor. */}
                            <p className="text-small text-gray-700">
                                CUI: {orderData.school?.cui || '-'} | Cod SE: {orderData.school?.codSe || '-'}
                            </p>
                        </div>

                        {/* Estimated vs Real Values Comparison */}
                        <div className="space-y-3">
                            <h3 className="font-semibold text-gray-700">Comparație Valori Estimate vs. Reale</h3>

                            {/* Albastrul (`bg-blue-50` / `text-blue-600`) e in afara paletei —
                                designul e verde de brand. `bg-muted` + `text-magenta` da acelasi
                                rol vizual (panou de citit, valoare accentuata) pe tokeni. */}
                            <div className="space-y-2 rounded-sm bg-muted p-3">
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Tip</span>
                                    </div>
                                    <div className="text-center">
                                        <span className="text-gray-600">Estimat (kg)</span>
                                    </div>
                                    <div className="text-center">
                                        <span className="text-gray-600">Real (kg)</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4 text-sm border-t border-gray-200 pt-2">
                                    <div className="font-medium">DEEE</div>
                                    <div className="text-center">{orderData.deeeKg || 0}</div>
                                    <div className="text-center font-semibold text-magenta">
                                        {form.watch('realDeeeKg') || 0}
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div className="font-medium">DBA</div>
                                    <div className="text-center">{orderData.dbaKg || 0}</div>
                                    <div className="text-center font-semibold text-magenta">
                                        {form.watch('realDbaKg') || 0}
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div className="font-medium">Becuri & Neoane</div>
                                    <div className="text-center">{orderData.becuriSiNeoaneKg || 0}</div>
                                    <div className="text-center font-semibold text-magenta">
                                        {form.watch('realBecuriSiNeoaneKg') || 0}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Real Values Input */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-gray-700">Valori Reale Colectate</h3>

                            {/* Trei campuri numerice pe un rand la 360px inseamna ~100px
                                fiecare, cu etichete rupte pe doua randuri. Sub `sm` merg
                                pe coloana; de la `sm` in sus raman cele trei coloane. */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                {/* DEEE Real */}
                                <FormField
                                    control={form.control}
                                    name="realDeeeKg"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="form-label-bold">DEEE Real (kg) *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    step="0.01"
                                                    disabled={loading}
                                                    placeholder="0.00"
                                                />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </FormItem>
                                    )}
                                />

                                {/* DBA Real */}
                                <FormField
                                    control={form.control}
                                    name="realDbaKg"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="form-label-bold">DBA Real (kg) *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    step="0.01"
                                                    disabled={loading}
                                                    placeholder="0.00"
                                                />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </FormItem>
                                    )}
                                />

                                {/* Becuri & Neoane Real */}
                                <FormField
                                    control={form.control}
                                    name="realBecuriSiNeoaneKg"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="form-label-bold">Becuri & Neoane Real (kg) *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    step="0.01"
                                                    disabled={loading}
                                                    placeholder="0.00"
                                                />
                                            </FormControl>
                                            <FormMessage className="form-message-spacer" />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {/* Total */}
                            <div className="bg-gray-50 p-3 rounded-sm">
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-gray-700">Total Real Colectat:</span>
                                    <span className="text-xl font-bold text-success">
                                        {total.toFixed(2)} kg
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Process Documents */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-gray-700">Documente Proces</h3>

                            <div className="space-y-3">
                                {/* DEEE Document */}
                                <div>
                                    <label className="mb-1 block text-small font-medium text-gray-700">
                                        Document Proces DEEE
                                    </label>
                                    {deeeDoc ? (
                                        <div className="flex items-center gap-2 p-2 border border-border rounded-md bg-gray-50">
                                            <a
                                                href={deeeDoc}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 truncate text-small text-magenta underline"
                                            >
                                                Vezi Document DEEE
                                            </a>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={removeDeeeDocument}
                                                disabled={loading}
                                                className="text-error hover:bg-error/10"
                                            >
                                                <FaTrash className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleDeeeUpload}
                                            disabled={loading || uploadingDeee}
                                            className="w-full border-2 border-dashed"
                                        >
                                            <FaUpload className="w-4 h-4 mr-2" />
                                            {uploadingDeee ? 'Se încarcă...' : 'Încarcă Document'}
                                        </Button>
                                    )}
                                </div>

                                {/* DBA Document */}
                                <div>
                                    <label className="mb-1 block text-small font-medium text-gray-700">
                                        Document Proces DBA
                                    </label>
                                    {dbaDoc ? (
                                        <div className="flex items-center gap-2 p-2 border border-border rounded-md bg-gray-50">
                                            <a
                                                href={dbaDoc}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 truncate text-small text-magenta underline"
                                            >
                                                Vezi Document DBA
                                            </a>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={removeDbaDocument}
                                                disabled={loading}
                                                className="text-error hover:bg-error/10"
                                            >
                                                <FaTrash className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleDbaUpload}
                                            disabled={loading || uploadingDba}
                                            className="w-full border-2 border-dashed"
                                        >
                                            <FaUpload className="w-4 h-4 mr-2" />
                                            {uploadingDba ? 'Se încarcă...' : 'Încarcă Document'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                                Anulare
                            </Button>
                            <Button type="submit" disabled={loading}>
                                {loading ? 'Se salvează...' : 'Actualizează Valori'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}


